/**
 * Laboratory recognition of a file: the Files row's words for where it stands,
 * the replacement question, the progress and page-choice stops, cancellation
 * and why a recognition stopped. The review of the draft itself is
 * `laboratory.ts`.
 */
export const laboratoryRecognition = {
  'lab.recognition.notLaboratoryFile': 'This file is not classified as laboratory results.',
  'lab.recognition.batchRecognize': 'Recognize selected files ({count})',
  'lab.recognition.workspaceTitle': 'Recognize laboratory results',
  'lab.recognition.backToFiles': 'Back to laboratory files',
  'lab.recognition.batchSummary': 'Files: {files} · Pages: {pages} · Selected: {selected}',
  'lab.recognition.batchFiles': 'Files in this recognition',
  'lab.recognition.recognizeBatch': 'Recognize selected pages',
  'lab.recognition.pagesPanel': 'Document pages',
  'lab.recognition.showPages': 'Show panel',
  'lab.recognition.hidePages': 'Hide panel',
  'lab.recognition.showPage': 'Show page {number} of {name}',
  'lab.recognition.includePage': 'Include page {number} in recognition',
  'lab.recognition.rowRecognize': 'Recognize',
  'lab.recognition.notStarted': 'Recognition has not started.',
  'lab.recognition.rowRecognizeNamed': 'Recognize laboratory results in {name}',
  'lab.recognition.rowReading': 'Reading pages…',
  'lab.recognition.rowReadingNamed': 'Reading the pages of {name}',
  'lab.recognition.rowChoosePages': 'Choose pages',
  'lab.recognition.rowChoosePagesNamed': 'Choose the pages of {name} to recognize',
  'lab.recognition.rowRecognizing': 'Recognizing: {done} of {total}',
  'lab.recognition.rowRecognizingNamed': 'Recognizing {name}: {done} of {total} pages done',
  'lab.recognition.rowReview': 'Review draft',
  'lab.recognition.rowReviewNamed': 'Review the results recognized in {name}',
  'lab.recognition.rowFailed': 'Recognition stopped',
  'lab.recognition.rowFailedNamed': 'Recognition of {name} stopped: open it',
  'lab.recognition.rowConfirmed': 'Confirmed {date}',
  'lab.recognition.rowConfirmedNamed': 'Open the report from {name} confirmed {date}',
  'lab.recognition.rowAgain': 'Recognize again',
  'lab.recognition.rowAgainNamed': 'Recognize {name} again',
  'lab.recognition.starting': 'Starting recognition…',
  'lab.recognition.gone': 'This recognition no longer exists. It may have been cancelled elsewhere.',
  'lab.recognition.loadFailed':
    'The recognition could not be read. Return to the file list and open it again.',
  'lab.recognition.replaceTitle': 'Recognize this document again?',
  'lab.recognition.replaceDetail':
    'When the new recognition is confirmed, the results recognized from this document are removed from the chart. Results: {count}, report of {date}.',
  'lab.recognition.replaceKept':
    'Until then they stay in the chart, and cancelling the new recognition leaves them as they are. The document is recognized from scratch.',
  'lab.recognition.replaceKeep': 'Keep the current results',
  'lab.recognition.analysingDetail':
    'Reading the pages and finding the ones that carry laboratory results. You choose the pages to recognize next.',
  'lab.recognition.recognizingDetail': 'Recognizing the chosen pages.',
  'lab.recognition.progress': 'Pages recognized: {done} of {total}',
  'lab.recognition.keepsWorking':
    'You can leave this page. Recognition continues, and the file row shows when it needs you.',
  'lab.recognition.pagesTitle': 'Choose the pages to recognize',
  'lab.recognition.pagesDetail':
    'The pages that look like laboratory results are ticked. Only the ticked pages are recognized: untick a page that is not results, and tick one that was missed.',
  'lab.recognition.page': 'Page {number}',
  'lab.recognition.enlargePage': 'Enlarge page {number}',
  'lab.recognition.pageImage': 'Page {number} of the document',
  'lab.recognition.pageImageUnavailable': 'Preview unavailable',
  'lab.recognition.markLaboratory': 'Laboratory results',
  'lab.recognition.markOther': 'Other content',
  'lab.recognition.markNoText': 'No text found',
  'lab.recognition.chosenCount': 'Pages chosen: {count}',
  'lab.recognition.recognizeChosen': 'Recognize the chosen pages',
  'lab.recognition.addPages': 'Add pages',
  'lab.recognition.addPagesTitle': 'Add pages to the draft',
  'lab.recognition.addPagesDetail':
    'The pages already recognized stay in the draft, and its saved edits are kept. Choose the pages to recognize as well.',
  'lab.recognition.recognizeAdded': 'Recognize the added pages',
  'lab.recognition.backToDraft': 'Back to the draft',
  'lab.recognition.sending': 'Sending…',
  'lab.recognition.cannotRun': 'Recognizing pages requires permission to run AI functions.',
  'lab.recognition.pagesFailed': 'The page choice could not be sent. Try again.',
  'lab.recognition.failedTitle': 'Recognition stopped.',
  'lab.recognition.retry': 'Try again',
  'lab.recognition.retrying': 'Restarting…',
  'lab.recognition.retryFailed': 'Recognition could not be restarted. Try again.',
  'lab.recognition.cancel': 'Cancel recognition',
  'lab.recognition.cancelQuestion':
    'Cancel this recognition? What it has read so far is deleted. Confirmed results stay as they are.',
  'lab.recognition.cancelKeep': 'Keep recognizing',
  'lab.recognition.cancelConfirm': 'Cancel recognition',
  'lab.recognition.cancelling': 'Cancelling…',
  'lab.recognition.cancelFailed': 'Recognition could not be cancelled. Try again.',
  'lab.recognition.cancelChanged': 'The recognition state has changed. Reload it before cancelling.',
  'lab.recognition.cancelReload': 'Reload recognition state',
  'lab.recognition.cancelReloadFailed': 'The recognition state could not be loaded. Try reloading again.',
  'lab.recognition.cancelReloaded': 'The current recognition state is shown. Confirm cancellation again if you still want to discard it.',
  'lab.recognition.stageConflict':
    'This recognition has moved on elsewhere. Return to the file list and open it again.',
  'lab.recognition.failureDisabled':
    'Laboratory recognition is turned off for this clinic. An administrator can turn it on in the AI settings.',
  'lab.recognition.failureGoogle':
    'Google Document AI is not set up for this clinic. An administrator can add it in the AI settings.',
  'lab.recognition.failureTooLarge': 'The document is too large or has too many pages to recognize.',
  'lab.recognition.failureUnreadable': 'The pages of this document could not be read. Check that the file opens.',
  'lab.recognition.failureUnavailable': 'The original file is no longer available.',
  'lab.recognition.failureQuota': "The clinic's storage is full, so the pages could not be saved.",
  'lab.recognition.failureService': 'The recognition service did not answer. Try again later.',
  'lab.recognition.failureAnswer': 'The recognition answer could not be used. Try again.',
  'lab.recognition.failureUnknown': 'Recognition met an unexpected error. Try again, and tell an administrator if it repeats.',
} as const
